import {
  axios,
  fetch_entity_profiles,
  fetch_scoped_entity_types,
  get_request_config,
  hydrate_case_display_title,
  is_real_case_record,
  is_open_case_status,
  map_backend_case,
} from './company_cases_helpers';
import {
  normalize_key,
  normalize_text,
  read_session_current_user_id,
  to_array,
  to_number,
} from './company_cases_common';
import { with_assigned_owner } from './company_cases_owner_helpers';
import { fetch_requestor_application_assigned_users } from '@/services/requestor_portal_service';
import { GET_USERS } from '@/utils/system_constants/constant.js';

function project_params(project_id) {
  return project_id ? { project_id } : {};
}

function quarter_start(date = new Date()) {
  return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);
}

function add_months(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function percentage_change(current, previous) {
  if (!previous) return null;
  return Math.round(((current - previous) / previous) * 100);
}

function case_event_date(record = {}) {
  const value = [
    record?.closed_at,
    record?.completed_at,
    record?.resolved_at,
    record?.date_closed,
    record?.updated_at,
    record?.modified_at,
    record?.date_modified,
    record?.created_at,
    record?.date_created,
  ].find(Boolean);
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

function is_closed_case(record = {}) {
  const case_item = map_backend_case(record, {});
  return !is_open_case_status(case_item.status);
}

function is_date_in_range(date, start, end) {
  return Boolean(date) && date >= start && date < end;
}

async function fetch_profile_batch(entity_type, project_id) {
  try {
    const records = await fetch_entity_profiles({
      entity_type_id: entity_type.id,
      page_size: 100,
      page: 1,
      ...project_params(project_id),
    });
    return { entity_type, records };
  } catch {
    return { entity_type, records: [] };
  }
}

async function fetch_profile_batches(entity_types = [], project_id = null) {
  if (entity_types.length) {
    return Promise.all(entity_types.map((entity_type) => fetch_profile_batch(entity_type, project_id)));
  }

  const records = await fetch_entity_profiles({
    page_size: 100,
    page: 1,
    ...project_params(project_id),
  });
  return [{ entity_type: {}, records }];
}

function normalize_company_cases(profile_batches = [], users_by_id = new Map()) {
  return profile_batches
    .flatMap(({ records, entity_type }) => records
      .filter((record) => is_real_case_record(record, entity_type))
      .map((record) => ({
        ...map_backend_case(record, entity_type, users_by_id),
        raw_record: record,
      })))
    .sort((left, right) => Number(right.sort_time || 0) - Number(left.sort_time || 0));
}

function assignment_user_id(assignment = {}) {
  return to_number(
    typeof assignment === 'object'
      ? assignment?.assigned_to_id
        || assignment?.assigned_user_id
        || assignment?.user_id
        || assignment?.user?.id
        || assignment?.assigned_to?.id
        || assignment?.assigned_to?.user_id
        || assignment?.profile?.user_id
      : assignment
  );
}

function assignment_role_key(assignment = {}) {
  return normalize_key([
    assignment?.role_name,
    assignment?.role,
    assignment?.assigned_role,
    assignment?.assigned_profile_role?.role?.role,
  ].map(normalize_text).find(Boolean));
}

function is_coordinator_assignment(assignment = {}) {
  const role_key = assignment_role_key(assignment);
  return role_key.includes('coordinator') || role_key === 'system-owner';
}

function assignment_user_ids(assignments = []) {
  return to_array(assignments).map(assignment_user_id).filter(Boolean);
}

function case_assignment_user_ids(case_item = {}) {
  return [
    ...to_array(case_item?.assignment_user_ids).map(to_number).filter(Boolean),
    ...assignment_user_ids(case_item?.application_assigned_users),
  ];
}

function case_is_assigned_to_current_coordinator(case_item = {}, current_user_id = null) {
  if (!current_user_id) return true;

  const assignments = to_array(case_item?.application_assigned_users);
  const coordinator_assignments = assignments.filter(is_coordinator_assignment);
  if (coordinator_assignments.length) {
    return assignment_user_ids(coordinator_assignments).includes(current_user_id);
  }

  return case_assignment_user_ids(case_item).includes(current_user_id);
}

async function hydrate_case_assignments(case_item = {}) {
  const existing_assignments = to_array(case_item?.application_assigned_users);
  if (existing_assignments.length) {
    return with_assigned_owner(case_item, existing_assignments);
  }

  if (!case_item?.application_id) {
    return case_item;
  }

  try {
    const assignments = await fetch_requestor_application_assigned_users(case_item.application_id);
    return with_assigned_owner(case_item, assignments);
  } catch {
    return case_item;
  }
}

async function hydrate_case_titles(cases = []) {
  return Promise.all(cases.map((case_item) => hydrate_case_display_title(case_item)));
}

async function hydrate_case_assignments_only(cases = []) {
  return Promise.all(cases.map(hydrate_case_assignments));
}

async function fetch_users_directory(project_id = null) {
  try {
    const response = await axios.get(GET_USERS, get_request_config({
      params: {
        page: 1,
        page_size: 500,
        ...(project_id ? { project_id } : {}),
      },
    }));
    const payload = response?.data?.data;
    const rows = Array.isArray(payload)
      ? payload
      : Array.isArray(response?.data)
        ? response.data
        : [];

    return new Map(
      rows
        .map((row) => {
          const user_id = Number(row?.user_id || row?.id || row?.user?.id);
          return Number.isFinite(user_id) && user_id > 0
            ? [user_id, row?.user || row]
            : null;
        })
        .filter(Boolean)
    );
  } catch {
    return new Map();
  }
}

export async function fetch_company_cases(project_id = null) {
  const { entity_types } = await fetch_scoped_entity_types(project_id);
  const [profile_batches, users_by_id] = await Promise.all([
    fetch_profile_batches(entity_types, project_id),
    fetch_users_directory(project_id),
  ]);
  return hydrate_case_titles(normalize_company_cases(profile_batches, users_by_id));
}

export async function fetch_coordinator_assigned_company_cases(project_id = null) {
  const current_user_id = read_session_current_user_id();
  const [records, users_by_id] = await Promise.all([
    fetch_entity_profiles({
      page_size: 100,
      page: 1,
      assigned_to_current_user: true,
      ...project_params(project_id),
    }),
    fetch_users_directory(project_id),
  ]);
  const cases = normalize_company_cases([{ entity_type: {}, records }], users_by_id);
  const hydrated_cases = await hydrate_case_assignments_only(cases);
  return hydrated_cases.filter((case_item) =>
    case_is_assigned_to_current_coordinator(case_item, current_user_id)
  );
}

export async function fetch_open_company_cases(project_id = null) {
  const cases = await fetch_company_cases(project_id);
  return cases.filter((case_item) => is_open_case_status(case_item.status));
}

export async function fetch_open_coordinator_assigned_company_cases(project_id = null) {
  const cases = await fetch_coordinator_assigned_company_cases(project_id);
  return cases.filter((case_item) => is_open_case_status(case_item.status));
}

export async function fetch_open_company_case_count(project_id = null) {
  const cases = await fetch_open_company_cases(project_id);
  return cases.length;
}

export async function fetch_open_coordinator_assigned_company_case_count(project_id = null) {
  const cases = await fetch_open_coordinator_assigned_company_cases(project_id);
  return cases.length;
}

export async function fetch_closed_case_summary(project_id = null) {
  const cases = await fetch_company_cases(project_id);
  const closed_cases = cases.filter((case_item) => is_closed_case(case_item.raw_record));
  const current_quarter_start = quarter_start(new Date());
  const next_quarter_start = add_months(current_quarter_start, 3);
  const previous_quarter_start = add_months(current_quarter_start, -3);

  const current_quarter_count = closed_cases.filter((case_item) =>
    is_date_in_range(case_event_date(case_item.raw_record), current_quarter_start, next_quarter_start)
  ).length;
  const previous_quarter_count = closed_cases.filter((case_item) =>
    is_date_in_range(case_event_date(case_item.raw_record), previous_quarter_start, current_quarter_start)
  ).length;

  return {
    current_quarter_count,
    previous_quarter_count,
    change_percentage: percentage_change(current_quarter_count, previous_quarter_count),
  };
}
